import { createContext } from 'react';

import type { MeResponse } from './widget-api/auth-me';
import type {
  GlobalWidgetConfigResponse,
  PublicOrganizationWithBranchesItem,
  PublicWidgetResponse
} from './widget-api/public-widget';
import type { EmbedMode } from './widget-lib/embed-session-store';

export type EmbedBootstrapValue = {
  orgSlug: string | null;
  /**
   * Студии приложения из `org_slugs=a,b` (или одна студия из `org_slug`).
   * Уходят в запросы входа как `org_slugs`; пусто — глобальное приложение без студии.
   */
  orgSlugs: string[];
  branchId: number | null;
  widgetConfig: PublicWidgetResponse | null;
  /** При `embed_global=1` без org_slug — из GET /public/widget-global (заголовок, цвет до выбора организации). */
  globalWidgetConfig: GlobalWidgetConfigResponse | null;
  /**
   * Разрешён ли бренд-режим (single org) по `GET /public/widget` — только при действующем тарифе и флаге плана.
   * `null`, если конфиг виджета ещё не загружен (нет key/подписи в URL).
   */
  brandEmbedAllowed: boolean | null;
  /** Из виджета или из сессии после client-login; при витрине глобального виджета — из `guest_org_id` в URL. */
  organizationId: number | null;
  /**
   * Глобальная витрина: организация выбрана гостем по `guest_org_id`, без GET /public/widget/{slug}.
   * Публичные поля для шапки и расписания.
   */
  guestShowcaseOrg: PublicOrganizationWithBranchesItem | null;
  /** Загрузка списка org для резолва `guest_org_id`. */
  guestShowcaseLoading: boolean;
  guestShowcaseError: string | null;
  /** Узкий гостевой просмотр (расписание и т.д.) без подписанной ссылки студии. */
  guestShowcaseBrowse: boolean;
  /** Режим сессии: клиент виджета или тренер (staff JWT). */
  embedMode: EmbedMode;
  /** Контур виджета из widget_name организации: определяет shell (booking/client/trainer/trainer-mobile/mobile). null до загрузки конфига. */
  widgetContour: 'booking' | 'client' | 'trainer' | 'trainer-mobile' | 'mobile' | null;
  accessToken: string | null;
  /** Профиль с сервера при наличии токена (источник правды для роли — см. ADR-0009). */
  me: MeResponse | null;
  meLoading: boolean;
  meError: string | null;
  /**
   * Обновить me. С аргументом — сразу записать свежий профиль (без повторного запроса);
   * без аргумента — перезапросить GET /auth/me.
   * В обработчики передавать обёрткой: `onClick={() => reloadMe()}`, а не ссылкой —
   * иначе событие клика прилетит первым аргументом.
   */
  reloadMe: (next?: MeResponse) => void;
  permissions: string[];
  /** `me.capabilities.modules_effective` при сессии, иначе `me.modules` из GET /auth/me. */
  effectiveModules: Record<string, boolean> | null;
  permissionsLoading: boolean;
  permissionsError: string | null;
  hasPermission: (permissionCode: string) => boolean;
  loading: boolean;
  /**
   * Полноэкранный прелоадер оболочки: ждём публичный конфиг виджета / глобальный widget-global / витрину guest_org_id.
   * Не показывается при фоновом reload, если данные уже есть (как списки в админке).
   */
  shellBootstrapLoader: boolean;
  error: string | null;
  reload: () => void;
  /** Подсказка, если в URL не хватает slug или ключа/подписи (при отсутствии сессии) */
  bootstrapHint: string | null;
  /**
   * Можно запросить публичный конфиг виджета: в URL есть org_slug и key или kid/exp/sig.
   * Даёт «анонимный» доступ к экранам без JWT (расписание и т.д. по правилам API).
   */
  canFetchWidget: boolean;
  unpaidBookingsCount: number;
  reloadBookings: () => void;
};

export const EmbedBootstrapContext = createContext<EmbedBootstrapValue | null>(null);
